import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { LayoutGrid, List, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { Button, ButtonShadcn } from "@/components/ui/Button";
import Select from "@/components/ui/Select";
import { useDebounce } from "@/hooks/useDebounce";
import { JobService } from "@/services/jobService";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CandidateService } from "@/services/candidateService";
import JobCard from "./component/JobCard";
import { JobCardSkeleton } from "./component/LoadingSkeleton";
import PostJobModal from "./component/PostJobModal";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import useModuleAccess from "@/hooks/useModuleAccess";
import AIModal from "./AIModal/AIModal";
import ManualJobPost from "./component/ManualJobPost";
import EditJobForm from "./EditJobForm";
import CloneJobForm from "./CloneJobForm";
import RetriggerCandidatesModal from "./components/RetriggerCandidatesModal";
import { MatchRetriggerService } from "@/services/matchRetriggerService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import SearchInput from "@/components/shared/SearchInput";
import Pagination from "@/helper/Pagination";
import useLiveResource from "@/hooks/useLiveResource";
import { Filter, FilterPill } from "@/components/shared/Filter";
import { EMPLOYEE_TYPE, JOB_STATUS_LIST } from "@/config/dropdownData";
import AlertModal from "@/components/shared/AlertModal";
import { useAuth } from "@/hooks/useAuth";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { UserService } from "@/services/userService";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import CalendarTypeService from "@/services/calendarTypeService";

const CLEAR_FILTERS = {
  status: "",
  department: "",
  division: "",
  location: "",
  employee_type: "",
  deadline_from: "",
  deadline_to: "",
  vacancy: "",
  hired: "",
  remaining: "",
  applicants: "",
  calendar_type_id: "",
};

const TABS = [
  { id: 'all-job', name: "All Jobs" },
  { id: 'assigned-job', name: "Assigned Jobs" },
]

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const SKELETON_ROWS = [
  { id: "job-skeleton-1" },
  { id: "job-skeleton-2" },
  { id: "job-skeleton-3" },
  { id: "job-skeleton-4" },
  { id: "job-skeleton-5" },
  { id: "job-skeleton-6" },
  { id: "job-skeleton-7" },
  { id: "job-skeleton-8" },
  { id: "job-skeleton-9" },
  { id: "job-skeleton-10" },
];

const getJobIdentifier = (job) => String(job?.id ?? job?.job_id ?? "");

const getStatusSubtitle = (totalJobs, isLoading) => {
  if (isLoading) {
    return "Loading postings…";
  }

  return `${totalJobs} Posting${totalJobs !== 1 ? "s" : ""}`;
};

const buildFilterMap = (filters) =>
  filters
    .flatMap((section) => section.children || [])
    .reduce((acc, filter) => {
      acc[filter.key] = filter;
      return acc;
    }, {});

const Job = () => {
  const setHeader = useOutletContext();
  const navigate = useNavigate();
  const { user } = useAuth()
  const [searchParams] = useSearchParams();

  const [totalJobs, setTotalJobs] = useState(0);
  const [jobList, setJobList] = useState([]);
  const [users, setUsers] = useState([]);
  const [calendarTypeOptions, setCalendarTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeModal, setActiveModal] = useState(null);
  const { isEnabled: jdModuleEnabled } = useModuleAccess(MODULES.AI_JD_IMPROVER);
  const [aiStep, setAiStep] = useState(1);
  const [editingJobId, setEditingJobId] = useState(null);
  const [cloningJobId, setCloningJobId] = useState(null);
  const [expandedCandidates, setExpandedCandidates] = useState({});
  const [candidatesData, setCandidatesData] = useState({});
  // { jobId, changedFields } — changedFields is carried through to the batch
  // so the audit ledger records WHY the re-analysis was requested.
  const [retriggerPicker, setRetriggerPicker] = useState(null);
  // Tracks a queued re-analysis batch so the user gets progress + a completion
  // summary after the picker closes. Keyed by batchId because the batch-level WS
  // events carry only that id.
  const [retriggerBatch, setRetriggerBatch] = useState(null);
  // Mirror of the above for WS handlers: they need the current batch without
  // re-subscribing on every counter tick, and must not read it from inside a
  // setState updater (updaters have to stay pure).
  const retriggerBatchRef = useRef(null);
  // Read inside the refetch below, which must not resubscribe every time a
  // card is expanded.
  const expandedCandidatesRef = useRef(expandedCandidates);
  useEffect(() => {
    expandedCandidatesRef.current = expandedCandidates;
  }, [expandedCandidates]);
  useEffect(() => {
    retriggerBatchRef.current = retriggerBatch;
  }, [retriggerBatch]);
  const [candidatesLoading, setCandidatesLoading] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all-job');
  const [restrictVisibility, setRestrictVisibility] = useState(false);


  const statusFromURL = searchParams.get("status");

  const [filtersState, setFiltersState] = useState({
    ...CLEAR_FILTERS,
    status: statusFromURL || "",
  });

  const debouncedFilters = useDebounce(filtersState);
  const debouncedSearchText = useDebounce(searchText);

  const currentPageRef = useRef(currentPage);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const fetchAllUsers = async () => {
    try {
      const res = await UserService.fetchUsersList({
        pagination: false,
        isActive: true,
        isDeleted: false
      });
      const list = res?.data?.users ?? [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Failed to fetch users for assignedTo:", e);
      setUsers([]);
    }
  };

  const fetchAllCalendarTypes = async () => {
    try {
      const all = await CalendarTypeService.listAll();
      setCalendarTypeOptions(
        all.map((c) => ({
          value: c.id,
          label: `${c.name}${c.isActive ? "" : " — disabled"}`,
        })),
      );
    } catch (e) {
      console.error("Failed to fetch calendar types for filter:", e);
      setCalendarTypeOptions([]);
    }
  };

  const fetchVisibilityMode = useCallback(() => {
    TenantSettingsService.getSettings()
      .then((res) => setRestrictVisibility(res?.data?.restrictDataVisibility))
      .catch(console.error);
  }, []);

  useEffect(() => {
    // Fetch all list of users
    fetchAllUsers();
    fetchAllCalendarTypes();

    fetchVisibilityMode();
  }, [fetchVisibilityMode]);

  // The org's data-visibility mode is edited on another screen (Organization
  // Settings) and decides what this list shows. Read once on mount, an admin
  // flipping PRIVATE mode left every other tab on the old rules until someone
  // reloaded (G1). `tenant_config.updated` is unfiltered — it arrives whether
  // or not this connection asked for the key — and the subscription is sent
  // anyway so every call site in the app reads the same way.
  useLiveResource({
    keys: "tenant_config",
    onChange: fetchVisibilityMode,
  });

  const fetchJobs = useCallback(
    async ({ silent = false, page } = {}) => {
      if (!silent) {
        setJobList([]);
        setIsLoading(true);
      }

      // Guards against out-of-order responses: mount/filter changes, a
      // toggle's own refresh, and the websocket `onChange` can all call
      // fetchJobs() concurrently, and without this a stale response landing
      // after a newer one would silently overwrite it.
      const seq = ++fetchSeqRef.current;

      try {
        const response = await JobService.fetchAdminJobDetails({
          page: page ?? currentPageRef.current,
          perPage: pageSize,
          search: debouncedSearchText,
          ...(activeTab === "assigned-job" ? { assignedJob: user.id } : {}),
          ...debouncedFilters,
        });

        if (seq !== fetchSeqRef.current) return;

        setTotalJobs(response?.data?.pagination?.total ?? 0);
        setJobList(response?.data?.jobs ?? []);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        console.error(err);
        setJobList([]);
      } finally {
        if (seq === fetchSeqRef.current) setIsLoading(false);
      }
    },
    [pageSize, activeTab, debouncedSearchText, debouncedFilters],
  );

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    currentPageRef.current = 1;
    setCurrentPage(1);
  }, [debouncedSearchText, debouncedFilters, pageSize]);

  const refreshCandidatesForJob = useCallback(async (jobId) => {
    try {
      const res = await CandidateService.fetchTop5Candidates(jobId);
      setCandidatesData((prev) => ({
        ...prev,
        [jobId]: res?.data?.candidates ?? [],
      }));
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── What is on screen, refetched ─────────────────────────────────────────
  //
  // One refetch of the list, plus the top-5 panel of every expanded card. The
  // events that used to patch a row carried `status`, `new_status` and `title`;
  // the envelope carries ids, and the list is filtered, paged and sorted by
  // the server anyway — so a row patched by hand was already a second, worse
  // copy of the server's answer.
  const refreshVisible = useCallback(() => {
    fetchJobs({ silent: true });
    setTogglingId(null);
    Object.entries(expandedCandidatesRef.current || {}).forEach(([jobId, isOpen]) => {
      if (isOpen) refreshCandidatesForJob(jobId);
    });
  }, [fetchJobs, refreshCandidatesForJob]);

  // Five resources, one refetch. A bulk run that scores twenty candidates on
  // three jobs costs one list refetch, not twenty.
  useLiveResource({
    keys: ["job", "application", "candidate", "match", "bulk_batch"],
    onChange: refreshVisible,
  });

  // ── Re-analysis batch progress ───────────────────────────────────────────
  //
  // Only the tab that queued the batch shows the panel — `retriggerBatch` is
  // set by the modal — so `started` marks the tracked batch running and an
  // event for anyone else's batch is dropped.
  const finishRetriggerBatch = useCallback(
    async (batchId) => {
      const tracked = retriggerBatchRef.current;
      if (!tracked || (batchId && tracked.batchId !== batchId)) return;

      // The batch row is the only source of `skipped`, and of counts that are
      // right whichever ticks this tab missed.
      let detail = null;
      try {
        detail = await MatchRetriggerService.getBatch(tracked.batchId);
      } catch (err) {
        console.error("Failed to read the finished re-analysis batch:", err);
      }

      const completed = detail?.completedCount ?? tracked.done;
      const failed = detail?.failedCount ?? tracked.failed;
      const skipped = detail?.skippedCount ?? 0;
      const parts = [`${completed} re-analysed`];
      if (failed) parts.push(`${failed} failed`);
      if (skipped) parts.push(`${skipped} skipped`);
      const summary = parts.join(", ");
      if (failed) {
        toast.error(`Re-analysis finished: ${summary}.`, { position: "top-right" });
      } else {
        toast.success(`Re-analysis complete — ${summary}.`, { position: "top-right" });
      }

      setRetriggerBatch(null);
      fetchJobs({ silent: true });
    },
    [fetchJobs],
  );

  useLiveResource({
    keys: "match_retrigger_batch",
    match: (event) => event.id === retriggerBatchRef.current?.batchId,
    onEvent: (event) => {
      if (event.event === "match_retrigger_batch.started") {
        setRetriggerBatch((prev) => (prev ? { ...prev, running: true } : prev));
        return;
      }
      if (event.event === "match_retrigger_batch.progress") {
        // Assigned, never incremented: these are the batch row's own counts,
        // so a tick this tab never saw costs nothing.
        const { done, total, failed } = event.progress ?? {};
        setRetriggerBatch((prev) =>
          prev
            ? {
              ...prev,
              done: done ?? prev.done,
              total: total ?? prev.total,
              failed: failed ?? prev.failed,
            }
            : prev,
        );
        return;
      }
      if (event.event === "match_retrigger_batch.done") finishRetriggerBatch(event.id);
    },
    // The reconnect and the gap are what the 5-second poll was really for: a
    // panel driven only by events sticks at its last count when a frame is
    // lost. One read of the batch on reconnect replaces the poll.
    onChange: () => {
      const tracked = retriggerBatchRef.current;
      if (!tracked?.batchId) return;
      MatchRetriggerService.getBatch(tracked.batchId)
        .then((detail) => {
          if (!detail) return;
          const terminal = detail.status && detail.status !== "QUEUED" && detail.status !== "RUNNING";
          if (terminal) {
            finishRetriggerBatch(detail.batchId);
            return;
          }
          setRetriggerBatch((prev) =>
            prev && prev.batchId === detail.batchId
              ? {
                ...prev,
                done:
                  (detail.completedCount ?? 0) +
                  (detail.failedCount ?? 0) +
                  (detail.skippedCount ?? 0) +
                  (detail.cancelledCount ?? 0),
                failed: detail.failedCount ?? prev.failed,
                total: detail.totalCount ?? prev.total,
              }
              : prev,
          );
        })
        .catch((err) => console.error("Failed to reconcile re-analysis batch:", err));
    },
  });

  const handleCancelRetriggerBatch = useCallback(async () => {
    const tracked = retriggerBatchRef.current;
    if (!tracked?.batchId || tracked.cancelling) return;

    setRetriggerBatch((prev) => (prev ? { ...prev, cancelling: true } : prev));
    try {
      const res = await MatchRetriggerService.cancelBatch(tracked.batchId);
      // Items already mid-flight finish — the LLM tokens are spent either way,
      // so say what actually stopped rather than implying everything did.
      toast.success(
        res?.cancelledCount
          ? `Cancelled ${res.cancelledCount} queued candidate(s). Any already in progress will finish.`
          : "Nothing left to cancel — every candidate is already in progress or done.",
        { position: "top-right" },
      );
    } catch (err) {
      console.error("Failed to cancel re-analysis batch:", err);
      toast.error(apiErrorMessageFormat(err, "Failed to cancel re-analysis"), {
        position: "top-right",
      });
      setRetriggerBatch((prev) => (prev ? { ...prev, cancelling: false } : prev));
    }
  }, []);

  const filters = useMemo(
    () => [
      {
        key: "status_employee",
        type: "row",
        children: [
          {
            key: "status",
            type: "select",
            label: "All Statuses",
            displayLabel: "Status",
            value: filtersState.status,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, status: val })),
            options: JOB_STATUS_LIST,
          },
          {
            key: "employee_type",
            type: "select",
            label: "Employee Type",
            displayLabel: "Employee Type",
            value: filtersState.employee_type,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, employee_type: val })),
            options: EMPLOYEE_TYPE,
          },
        ],
      },
      {
        key: "dept_div",
        type: "row",
        children: [
          {
            key: "department",
            type: "input",
            label: "Department",
            displayLabel: "Department",
            value: filtersState.department,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, department: val })),
          },
          {
            key: "division",
            type: "input",
            label: "Division",
            displayLabel: "Division",
            value: filtersState.division,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, division: val })),
          },
        ],
      },
      {
        key: "job_stats_1",
        type: "row",
        children: [
          {
            key: "vacancy",
            type: "input",
            label: "Total Openings",
            displayLabel: "Vacancy",
            value: filtersState.vacancy,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, vacancy: val })),
          },
          {
            key: "hired",
            type: "input",
            label: "Hired",
            displayLabel: "Hired",
            value: filtersState.hired,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, hired: val })),
          },
        ],
      },
      {
        key: "job_stats_2",
        type: "row",
        children: [
          {
            key: "remaining",
            type: "input",
            label: "Available",
            displayLabel: "Remaining",
            value: filtersState.remaining,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, remaining: val })),
          },
          {
            key: "applicants",
            type: "input",
            label: "Applicants",
            displayLabel: "Applicants",
            value: filtersState.applicants,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, applicants: val })),
          },
        ],
      },
      {
        key: "deadline_range",
        type: "row",
        children: [
          {
            key: "deadline_from",
            type: "date",
            displayLabel: "Deadline from",
            placeholder: "Deadline from",
            value: filtersState.deadline_from,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, deadline_from: val })),
          },
          {
            key: "deadline_to",
            type: "date",
            displayLabel: "Deadline to",
            placeholder: "Deadline to",
            minDate: filtersState.deadline_from,
            value: filtersState.deadline_to,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, deadline_to: val })),
          },
        ],
      },
      {
        key: "location_row",
        type: "row",
        children: [
          {
            key: "location",
            type: "input",
            label: "Location",
            displayLabel: "Location",
            value: filtersState.location,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, location: val })),
          },
          {
            key: "calendar_type_id",
            type: "select",
            label: "Calendar",
            displayLabel: "Calendar",
            value: filtersState.calendar_type_id,
            onChange: (val) =>
              setFiltersState((prev) => ({ ...prev, calendar_type_id: val })),
            options: calendarTypeOptions,
          },
        ],
      },
    ],
    [filtersState, calendarTypeOptions],
  );

  const filterMap = useMemo(() => buildFilterMap(filters), [filters]);

  const activeFilters = useMemo(
    () =>
      Object.entries(filtersState)
        .filter(([, value]) => value)
        .map(([key, value]) => {
          const filter = filterMap[key];
          let displayValue = value;

          if (filter?.options) {
            displayValue =
              filter.options.find((option) => option.value === value)?.label ??
              value;
          }

          return {
            key,
            label: `${filter?.displayLabel || filter?.label || key}: ${displayValue}`,
            clear: () =>
              setFiltersState((prev) => ({
                ...prev,
                [key]: "",
              })),
          };
        }),
    [filtersState, filterMap],
  );

  const clearAllFilters = useCallback(() => {
    setFiltersState(CLEAR_FILTERS);
    setShowFilters(false);
  }, []);

  const handleCloseAllModals = useCallback(() => {
    setActiveModal(null);
    setAiStep(1);
  }, []);

  const handleStatusChange = useCallback(
    async (jobId) => {
      setTogglingId(jobId);

      try {
        const currentJob = jobList.find((job) => job.id === jobId);
        if (!currentJob) return;

        if (currentJob.status !== "OPEN") {
          const isDeadlinePassed =
            currentJob.deadline &&
            new Date(currentJob.deadline).getTime() < Date.now();

          const isVacancyFilled =
            (currentJob.hired_count ?? 0) >= (currentJob.vacancy_count ?? 0) &&
            currentJob.vacancy_count > 0;

          if (isDeadlinePassed && isVacancyFilled) {
            setActiveModal("BOTH_ALERT");
            setEditingJobId(jobId);
            return;
          }

          if (isDeadlinePassed) {
            setActiveModal("DEADLINE_ALERT");
            setEditingJobId(jobId);
            return;
          }

          if (isVacancyFilled) {
            setActiveModal("FILLED_ALERT");
            setEditingJobId(jobId);
            return;
          }
        }
        const nextStatus = currentJob.status === "OPEN" ? "CLOSED" : "OPEN";
        await JobService.jobStatusChange(jobId, nextStatus);
        fetchJobs({ silent: true });
      } catch (err) {
        // The three alert modals above divert on the numbers this card is
        // holding, which can be stale — another recruiter's hire, or a
        // deadline that passed while this tab sat open. The server refuses
        // those with JOB_REOPEN_BLOCKED, and this is the only place that says
        // so; without it the toggle sprang back and nothing explained why.
        toast.error(apiErrorMessageFormat(err, "Failed to change the job's status"), {
          position: "top-right",
        });
      } finally {
        setTogglingId(null);
      }
    },
    [jobList, fetchJobs],
  );

  const handleCandidateList = useCallback(
    async (jobId) => {
      const isOpen = expandedCandidates[jobId];
      setExpandedCandidates((prev) => ({ ...prev, [jobId]: !isOpen }));

      if (isOpen || candidatesData[jobId]) return;

      setCandidatesLoading((prev) => ({ ...prev, [jobId]: true }));

      try {
        const res = await CandidateService.fetchTop5Candidates(jobId);
        setCandidatesData((prev) => ({
          ...prev,
          [jobId]: res?.data?.candidates || [],
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setCandidatesLoading((prev) => ({ ...prev, [jobId]: false }));
      }
    },
    [expandedCandidates, candidatesData],
  );

  const handleEdit = useCallback((jobId) => {
    setEditingJobId(jobId);
    setActiveModal("EDIT");
  }, []);

  const closeEditModal = useCallback(() => {
    setActiveModal(null);
    setEditingJobId(null);
  }, []);

  const handleClone = useCallback((jobId) => {
    setCloningJobId(jobId);
    setActiveModal("CLONE");
  }, []);

  const closeCloneModal = useCallback(() => {
    setActiveModal(null);
    setCloningJobId(null);
  }, []);

  const handleManualJobPost = useCallback(
    async (formData) => {
      try {
        setIsSubmitting(true);
        const skills = Array.isArray(formData.skills)
          ? formData.skills
          : (formData.skills || "")
            .split(",")
            .map((skill) => skill.trim())
            .filter(Boolean);

        await JobService.manualJobPost(formData, skills);
        toast.success("Job posted successfully.", { position: "top-right" });
        handleCloseAllModals();
        fetchJobs({ silent: true });
      } catch (error) {
        console.error("Failed to post job:", error);
        toast.error(error.message, { position: "top-right" });
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchJobs, handleCloseAllModals],
  );

  useEffect(() => {
    setHeader({
      title: "Job Postings",
      subTitle: getStatusSubtitle(totalJobs, isLoading),
    });
  }, [setHeader, totalJobs, isLoading]);

  const userOptions = useMemo(() => {
    return users
      .map((u) => {
        const id = u?.id ?? u?._id;
        const labelName = u?.name || String(id ?? "");

        return {
          label: user.id === id ? "Me" : labelName,
          value: id ? String(id) : "",
        };
      });
  }, [users]);

  const isGrid = viewMode === "grid";
  const hasJobs = jobList.length > 0;
  const showEmptyState = !isLoading && !hasJobs;
  const jobsContainerClass = isGrid
    ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 py-1"
    : "space-y-3 py-1";

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  // A tenant without the AI Job Description module has one way to post a job,
  // so there is nothing to choose: Post a New Job opens the manual form
  // directly. The chooser still opens when the module is owned but currently
  // dark — that tenant *does* have two options, and the disabled AI card with
  // its reason is how they learn one of them is down right now.
  const handleOpenPostModal = useCallback(() => {
    setActiveModal(jdModuleEnabled ? "POST" : "MANUAL");
  }, [jdModuleEnabled]);

  const handleListView = useCallback(() => {
    setViewMode("list");
  }, []);

  const handleGridView = useCallback(() => {
    setViewMode("grid");
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      currentPageRef.current = page;
      setCurrentPage(page);
      fetchJobs({ silent: false, page });
    },
    [fetchJobs],
  );

  const handlePageSizeChange = useCallback((value) => {
    setPageSize(Number(value));
  }, []);

  return (
    <PermissionGuard permission={PERMISSIONS.JOB_VIEW}>
      <div className="flex flex-col h-[calc(100vh-80px)]">
        <div className="flex items-center gap-2 mb-2.5 shrink-0">
          <SearchInput
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Search by title or job ID..."
            className="w-48 md:w-64"
          />

          <div className="hidden md:flex items-center border border-border-primary rounded-lg p-1 bg-background-secondary">
            <Button
              onClick={handleListView}
              className={`p-1.5 rounded-md transition-all ${viewMode === "list"
                ? "bg-background-primary shadow-sm text-text-primary"
                : "text-text-secondary hover:text-text-primary"
                }`}
              title="List View"
            >
              <List size={14} />
            </Button>
            <Button
              onClick={handleGridView}
              className={`p-1.5 rounded-md transition-all ${viewMode === "grid"
                ? "bg-background-primary shadow-sm text-text-primary"
                : "text-text-secondary hover:text-text-primary"
                }`}
              title="Grid View"
            >
              <LayoutGrid size={14} />
            </Button>
          </div>

          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={handleToggleFilters}
            onClearAll={clearAllFilters}
            renderActiveFilter={(filter) => (
              <FilterPill
                key={filter.key}
                label={filter.label}
                onRemove={filter.clear}
              />
            )}
          />
          <PermissionGuard permission={PERMISSIONS.JOB_CREATE} isComponent>

            <Button
              onClick={handleOpenPostModal}
              className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2.5 items-center gap-1 text-[11px] whitespace-nowrap shrink-0"
            >
              <Plus size={11} />
              <span>Post New Job</span>
            </Button>
          </PermissionGuard>
        </div>

        {!restrictVisibility && (
          <div className="flex items-center w-fit bg-background-tab border border-border-primary rounded-xl px-1.5 py-1.5 shrink-0 gap-3 mb-1">
            {TABS.map((tab) => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center justify-center transition-all duration-150
                  gap-1.5 px-1.5 py-1 rounded-lg font-medium text-sm whitespace-nowrap
                  ${activeTab === tab.id
                    ? "bg-background-tertiary text-text-primary border border-text-base/10 shadow-sm"
                    : "border border-transparent text-text-secondary hover:text-text-primary"
                  }
                `}
              >
                <span className="inline">{tab.name}</span>
              </Button>
            ))}
          </div>
        )}

        <PermissionGuard permission={PERMISSIONS.JOB_CREATE} isComponent>
          {/* Keep the action above page content but below the mobile sidebar so
              its logout control remains reachable when the menu is open. */}
          <div className="fixed bottom-0 left-0 right-0 z-10 md:hidden px-4 py-3">
            <Button
              onClick={handleOpenPostModal}
              className="w-full bg-button-secondary text-text-base rounded-full py-3.5 flex items-center justify-center gap-1.5 text-[13px]"
            >
              <Plus size={14} />
              Post New Job
            </Button>
          </div>
        </PermissionGuard>

        <div
          className="scrollbar-hide flex-1 min-h-0 overflow-y-auto pr-1"
          style={{ zoom: 0.95 }}
        >
          <div className={jobsContainerClass}>
            {isLoading &&
              SKELETON_ROWS.slice(0, pageSize).map((row) => (
                <JobCardSkeleton key={row.id} viewMode={viewMode} />
              ))}

            {showEmptyState && (
              <div className="text-center text-gray-400 text-[13px] py-10 col-span-full">
                No jobs found
              </div>
            )}

            {hasJobs &&
              jobList.map((job) => (
                <JobCard
                  key={getJobIdentifier(job)}
                  job={job}
                  viewMode={viewMode}
                  isDataUpdate={togglingId === job.id}
                  handleStatusChange={handleStatusChange}
                  handleEdit={handleEdit}
                  handleClone={handleClone}
                  handleCandidateList={handleCandidateList}
                  expandedCandidates={expandedCandidates}
                  candidatesData={candidatesData}
                  candidatesLoading={candidatesLoading}
                />
              ))}
          </div>
        </div>

        <div className="md:p-0 pb-9 shrink-0" style={{ zoom: 0.95 }}>
          {totalJobs > 0 && (
            <div className="flex items-center justify-between gap-3 px-1 py-2.5 border-t border-border-primary">
              <div className="flex items-center gap-2 text-[12px] text-text-secondary">
                <span>Items per page</span>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  options={PAGE_SIZE_OPTIONS.map((size) => ({ label: String(size), value: size }))}
                  className="h-7 rounded-md py-1 pl-2 text-[12px]"
                />
              </div>

              <div className={isLoading ? "invisible" : "visible"}>
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalJobs}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  label="jobs"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <PostJobModal
        open={activeModal === "POST"}
        onClose={handleCloseAllModals}
        setActiveModal={setActiveModal}
        setAiStep={setAiStep}
      />

      {/* A tenant without the module never opens the chooser at all, and the
          chooser never offers AI when it does open. This is the last lock:
          `activeModal` cannot be set to "AI" by the UI, and if it ever were,
          nothing would mount.

          `passthrough` for the killed state. The opener in the chooser is
          already disabled there, so this modal cannot be reached; wrapping a
          dialog in a disabled shell would only produce a dialog nobody can
          close. */}
      <ModuleGate module={MODULES.AI_JD_IMPROVER} variant="passthrough">
      <AIModal
        open={activeModal === "AI"}
        onClose={handleCloseAllModals}
        aiStep={aiStep}
        setAiStep={setAiStep}
        onSuccess={() => fetchJobs({ silent: true })}
        onBack={() => {
          setActiveModal("POST");
          setAiStep(1);
        }}
        userOptions={userOptions}
      />
      </ModuleGate>

      <ManualJobPost
        open={activeModal === "MANUAL"}
        onClose={handleCloseAllModals} userOptions={userOptions}
        onBack={jdModuleEnabled ? () => setActiveModal("POST") : undefined}
        onSubmit={handleManualJobPost}
        isSubmitting={isSubmitting}
      />

      <EditJobForm
        open={activeModal === "EDIT"}
        jobId={editingJobId}
        onClose={closeEditModal}
        onSuccess={({ jobId, changedScoreFields } = {}) => {
          closeEditModal();
          toast.success("Job updated successfully.", { position: "top-right" });
          fetchJobs({ silent: true });
          // Never contingent on the save itself (E19) — the JD update above
          // already completed; this only decides whether to offer the
          // re-trigger picker as a follow-on action. Empty list => the edit
          // provably cannot move any match score, so no picker.
          if (changedScoreFields?.length && jobId) {
            setRetriggerPicker({ jobId, changedFields: changedScoreFields });
          }
        }}
        userOptions={userOptions}
      />

      <CloneJobForm
        open={activeModal === "CLONE"}
        jobId={cloningJobId}
        onClose={closeCloneModal}
        onSuccess={() => fetchJobs({ silent: true })}
        userOptions={userOptions}
      />

      <RetriggerCandidatesModal
        open={!!retriggerPicker?.jobId}
        jobId={retriggerPicker?.jobId}
        changedFields={retriggerPicker?.changedFields || []}
        onClose={() => setRetriggerPicker(null)}
        onQueued={({ batchId, queuedCount }) =>
          setRetriggerBatch({
            batchId,
            total: queuedCount,
            done: 0,
            failed: 0,
            // The batch_started event may arrive before or after this setState;
            // treat it as running immediately so early item events still count.
            running: true,
          })
        }
      />

      {retriggerBatch && (
        <div className="fixed bottom-4 right-4 z-50 w-72 rounded-lg border border-border-primary bg-background-secondary shadow-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-semibold text-text-primary">
              Re-analysing candidates…
            </span>
            <span className="text-[11px] text-text-secondary">
              {retriggerBatch.done}/{retriggerBatch.total}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-background-icon/40 overflow-hidden">
            <div
              className="h-full bg-button-secondary transition-all duration-300"
              style={{
                width: `${retriggerBatch.total
                  ? Math.min(100, (retriggerBatch.done / retriggerBatch.total) * 100)
                  : 0
                  }%`,
              }}
            />
          </div>
          {retriggerBatch.failed > 0 && (
            <div className="text-[11px] text-red-400 mt-1.5">
              {retriggerBatch.failed} failed
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <ButtonShadcn
              variant="link"
              size="xs"
              className="text-[11px] text-text-secondary"
              disabled={retriggerBatch.cancelling}
              onClick={handleCancelRetriggerBatch}
            >
              {retriggerBatch.cancelling ? "Cancelling…" : "Cancel remaining"}
            </ButtonShadcn>
          </div>
        </div>
      )}

      <AlertModal
        open={activeModal === "BOTH_ALERT"}
        onCancel={() => {
          setActiveModal(null);
          setEditingJobId(null);
        }}
        onAction={() => setActiveModal("EDIT")}
        title="Job Cannot Be Reopened"
        description="This job was automatically closed because all vacancies have been filled and the application deadline has passed. You can reopen the job and update the vacancy count and update the deadline before proceeding."
        titleClassName="text-text-primary"
        descriptionClassName="text-text-primary/70"
        cancelClassName="border-none text-black"
        actionClassName="!bg-button-secondary"
      />
      <AlertModal
        open={activeModal === "DEADLINE_ALERT"}
        onCancel={() => {
          setActiveModal(null);
          setEditingJobId(null);
        }}
        onAction={() => setActiveModal("EDIT")}
        title="Deadline Passed"
        description="This job was automatically closed because the application deadline has passed. You can reopen the job and update the deadline before proceeding."
        titleClassName="text-text-primary"
        descriptionClassName="text-text-primary/70"
        cancelClassName="border-none text-black"
        actionClassName="!bg-button-secondary"
      />
      <AlertModal
        open={activeModal === "FILLED_ALERT"}
        onCancel={() => {
          setActiveModal(null);
          setEditingJobId(null);
        }}
        onAction={() => setActiveModal("EDIT")}
        title="Vacancy Filled"
        description="This job was automatically closed because all vacancies have been filled. You can reopen the job and update the vacancy count before proceeding."
        titleClassName="text-text-primary"
        descriptionClassName="text-text-primary/70"
        cancelClassName="border-none text-black"
        actionClassName="!bg-button-secondary"
      />
    </PermissionGuard>
  );
};

export default Job;
